/** A map must show every place its block names — silently truncating a real
 *  block's places was rejected (session tweak issue 777jcad), so this ceiling
 *  is defensive only: far beyond anything a model actually writes, it exists
 *  purely so a hostile block can't enqueue an unbounded geocode run. Note the
 *  cost of the headroom: Nominatim allows 1 request/second for the whole
 *  deployment (spec D.2), so a cold block at this ceiling is a ~49-second
 *  serial resolve. */
export const MAP_MAX_PINS = 48;

/** Preferred pin separator — space-colon-colon-space. Matches the guide
 *  corpus models are taught to emit. A line is
 *  `place :: label :: description`, split into at most three parts: a fourth
 *  separator and everything after it stay inside the description, which is
 *  prose and will contain punctuation. */
export const MAP_PIN_SEPARATOR = ' :: ';
/** Bare fallback for a model that dropped the surrounding spaces
 *  (`Berlin::HQ`). Checked only when the spaced form above is absent — a
 *  model omitting the spaces is far more likely than a place name
 *  containing a literal `::`, and silently geocoding `"Berlin::HQ"` as one
 *  place would be the worse failure. See parse-map-block.ts findPinSeparator. */
export const MAP_PIN_SEPARATOR_BARE = '::';

/** Trailing TAGGED icon field: `icon: <keyword>`, matched against the line's
 *  LAST separator-delimited segment (after trimming it), case-insensitive and
 *  with flexible spacing around the colon. Tagged rather than positional so it
 *  can never collide with the description, which is prose and owns every
 *  untagged trailing segment: `A :: B :: C :: D` keeps `C :: D` as the
 *  description, while `A :: B :: C :: icon: hotel` peels the icon off before
 *  the three-field split. A valueless `icon:` segment does not match — the
 *  segment is trimmed first, so `.+` demands a real value — and stays ordinary
 *  field text. The keyword is display-only: the front end maps it to a glyph,
 *  and it is NEVER a lookup or geocoder input. */
export const MAP_PIN_ICON_FIELD_REGEX = /^icon\s*:\s*(.+)$/i;

export const MAP_DIRECTIVE_REGEX = /^(title|zoom|center|route)\s*:(.*)$/i;
export const MAP_COMMENT_PREFIX = '#';
/** One optional markdown bullet — models emit these out of habit. Matches a
 *  bullet followed by content (`- Berlin`) OR nothing at all (a lone `-`),
 *  so an empty bullet line is fully consumed rather than surviving as a
 *  one-character "place". */
export const MAP_BULLET_REGEX = /^[-*+](?:\s+|$)/;
export const MAP_COORDINATE_REGEX =
  /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;

export const MAP_LAT_MIN = -90;
export const MAP_LAT_MAX = 90;
export const MAP_LON_MIN = -180;
export const MAP_LON_MAX = 180;
export const MAP_ZOOM_MIN = 0;
export const MAP_ZOOM_MAX = 18;

/** Defensive ceiling on a single line, covering the whole line: place, label
 *  AND description together. It is deliberately NOT raised for the
 *  description — `GEO_MAX_QUERY_LENGTH` is derived from it, so raising it
 *  would widen what may be put on the wire to a geocoder for the sake of
 *  display prose. A place name plus a sentence about it is nowhere near this;
 *  it exists so a malformed or hostile block can't put a megabyte-scale
 *  string into the React render path or a BL geocode request. Lines over this
 *  are skipped outright, not truncated, so the drop is visible rather than
 *  silently mangling the pin. */
export const MAP_MAX_LINE_LENGTH = 200;

/** Defensive ceiling on the whole block. MAP_MAX_PINS (48) pins at
 *  MAP_MAX_LINE_LENGTH (200) chars each is ~9,600 characters, so this
 *  leaves headroom for directives, comments, and blank lines while still
 *  bounding the work `parseMapBlock` does on a pathological block — input
 *  past this point is never even split into lines. */
export const MAP_MAX_INPUT_LENGTH = 20_000;

// ---------------------------------------------------------------------------
// Fence recognition
//
// Two spellings reach `parseMapBlock`, and any consumer that reads a map out of
// stored markdown must accept BOTH or it silently sees half the maps models
// actually write:
//
//   1. The first-class ```map fence.
//   2. A ```mermaid fence whose body opens with a bare `map` line — models
//      reach for ```mermaid by reflex.
//
// The renderer resolves these in `apps/fe/src/components/ui/Cmarkdown/
// markdown-block-renderer.tsx` (info string) and `mermaid-visual-patterns.ts`
// (`MAP_HEADER_PATTERN`). The constants below are the same rules expressed once
// here so a non-React consumer (BL geocoding) claims exactly the same blocks.
// ---------------------------------------------------------------------------

/** Fence info string, compared after `trim().toLowerCase()` — the same
 *  normalization `getCodeLanguage` applies on the render side. */
export const MAP_FENCE_INFO_STRING = 'map';
export const MERMAID_FENCE_INFO_STRING = 'mermaid';

/**
 * The in-body `map` header.
 *
 * Anchored to the START of the block rather than to any line (no `/m`): `map`
 * is an ordinary English word that can appear as a node label deep inside an
 * unrelated fence, and claiming such a fence would turn a readable block into a
 * map. Only leading blank lines may precede it.
 *
 * Mirrors `MAP_HEADER_PATTERN` in `apps/fe/src/components/ui/Cmarkdown/
 * mermaid-visual-patterns.ts`.
 */
export const MAP_FENCE_HEADER_REGEX = /^(?:[ \t]*\r?\n)*[ \t]*map[ \t]*(?:\r?\n|$)/;

/** CommonMark fence opener: up to three leading spaces, then three or more
 *  backticks or tildes, then the info string. A backtick-fenced info string may
 *  not itself contain a backtick. */
export const MAP_FENCE_OPEN_REGEX = /^ {0,3}(?:(`{3,})([^`]*)|(~{3,})(.*))$/;

/** CommonMark fence closer: the same character, at least as many of them, and
 *  nothing else on the line. */
export const MAP_FENCE_CLOSE_REGEX = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;
